import {
  Arrow,
  Fill,
  FillConvertible,
  Font,
  GroupNodeStyle,
  HierarchicalLayoutRoutingStyle,
  IEdge,
  InteriorNodeLabelModel,
  LabelStyle,
  NodeStyleIndicatorRenderer,
  NodeStyleLabelStyleAdapter,
  PolylineEdgeStyle,
  RectangleNodeStyle,
  ShapeNodeStyle,
  Stroke
} from '@yfiles/yfiles';
import { DotEdgeStyle } from './DotEdgeStyle';
import {
  EDGE,
  HEAT_MAP,
  NODE,
  NODE_3D,
  PRODUCT_COLORS,
  PRODUCT_COLORS_LIGHT,
  TEXT,
  UI
} from './colors.ts';
import { Product } from './types.ts';
import { getRevenue } from './util.ts';

export const variableEdges = false;

export const constantEdgeWidth = 3;

// The total (white) edge background stroke width
export const edgeBackgroundStrokeWidth = 5;

// edge widths are scaled by revenue of the product
// to a maximum of this value
export const maximumEdgeWidth = 20;

export const fontFamily = 'Segoe UI';

export const edgeRoutingStyle = HierarchicalLayoutRoutingStyle.OCTILINEAR;

// node height for "3D" rendering when none is provided by the tag
export const defaultNodeHeight = 10;

// increase visible node size by this factor
export const nodeHeightExaggeration = 1.5;

// Note: 3D application node colors are defined below (lines ~216-217)

// Base app node style without heatmapping
export const appNodeStyle = new RectangleNodeStyle({
  cornerSize: 4,
  stroke: `1px ${NODE.STROKE.DEFAULT}`,
  fill: NODE.FILL.DEFAULT
});

/**
 * Get a leaf node style with heatmap coloring based on percentage
 * @param percentage - The percentage value as a string (e.g. '65%')
 * @param performanceMetric - The selected performance metric
 * @returns RectangleNodeStyle with appropriate heatmap coloring
 */
export function getLeafNodeStyle(
  percentage?: number,
  performanceMetric: string = 'Health'
): RectangleNodeStyle {
  // If Health or Time metric is selected or no percentage provided, return default style (no heatmapping)
  if (performanceMetric === 'Health' || performanceMetric === 'Time' || !percentage) {
    return appNodeStyle;
  }

  // Determine fill color based on percentage thresholds
  let fill = HEAT_MAP.FILL.DEFAULT;
  let stroke = `1px ${HEAT_MAP.STROKE.DEFAULT}`;

  if (percentage >= 90) {
    // Green for high percentage
    fill = HEAT_MAP.FILL.GOOD; // Light green background
    stroke = `1px ${HEAT_MAP.STROKE.GOOD}`; // Green border
  } else if (percentage >= 75) {
    // Yellow for medium percentage
    fill = HEAT_MAP.FILL.WARNING; // Light yellow background
    stroke = `1px ${HEAT_MAP.STROKE.WARNING}`; // Yellow border
  } else {
    // Red for low percentage
    fill = HEAT_MAP.FILL.BAD; // Light red background
    stroke = `1px ${HEAT_MAP.STROKE.BAD}`; // Red border
  }

  return new RectangleNodeStyle({
    cornerSize: 4,
    stroke: stroke,
    fill: fill
  });
}

export const columnLabelStyle = new LabelStyle({
  horizontalTextAlignment: 'center',
  verticalTextAlignment: 'center',
  backgroundStroke: UI.BACKGROUND.TRANSPARENT,
  backgroundFill: UI.BACKGROUND.TRANSPARENT,
  textFill: 'black',
  font: new Font({
    fontFamily,
    fontSize: 26,
    fontWeight: 'bold'
  }),
  wrapping: 'wrap-word-ellipsis'
});

export const columnHeaderStyle = new NodeStyleLabelStyleAdapter({
  nodeStyle: new ShapeNodeStyle({
    fill: UI.BACKGROUND.COLUMN_HEADER,
    stroke: UI.BACKGROUND.TRANSPARENT
  }),
  labelStyle: columnLabelStyle
});

export const folderNodeLabelStyle = new LabelStyle({
  textSize: 22,
  textFill: TEXT.LIGHT,
  wrapping: 'wrap-word-ellipsis',
  padding: 10,
  verticalTextAlignment: 'center',
  horizontalTextAlignment: 'center',
  font: new Font({
    fontFamily,
    fontSize: 24,
    fontWeight: 'normal'
  })
});

export const appNodeTitleLabelStyle = new LabelStyle({
  verticalTextAlignment: 'center',
  horizontalTextAlignment: 'left',
  font: new Font({
    fontFamily,
    fontSize: 10,
    fontWeight: '600'
  }),
  wrapping: 'wrap-word-ellipsis',
  textFill: '#323130',
  maximumSize: [180, 18] // Limit the overall size of the label
});

export const appNodeTitleLabelLayoutParameter = new InteriorNodeLabelModel({
  padding: { top: 9, left: 10, right: 0, bottom: 0 }
}).createParameter('top-left');

export const leafNameLabelStyle = new LabelStyle({
  verticalTextAlignment: 'center',
  horizontalTextAlignment: 'left',
  font: new Font({
    fontFamily,
    fontSize: 8,
    fontWeight: '400'
  }),
  wrapping: 'wrap-word-ellipsis',
  textFill: TEXT.MEDIUM,
  maximumSize: [120, 14] // Limit the overall size of the label
});

export const leafNameLabelLayoutParameter = new InteriorNodeLabelModel({
  padding: { top: 22, left: 10, right: 0, bottom: 0 }
}).createParameter('top-left');

export const percentageLabelStyle = new LabelStyle({
  verticalTextAlignment: 'center',
  horizontalTextAlignment: 'left',
  font: new Font({
    fontFamily,
    fontSize: 10,
    fontWeight: '400'
  }),
  wrapping: 'wrap-word-ellipsis',
  textFill: '#605e5c'
});

export const percentageLabelLayoutParameter = new InteriorNodeLabelModel({
  padding: { top: 0, left: 10, right: 0, bottom: 10 }
}).createParameter('bottom-left');

export const alarmLabelLayoutParameter = new InteriorNodeLabelModel({
  padding: { top: 0, left: 30, right: 0, bottom: 9 }
}).createParameter('bottom-left');

export const alarmHaloLabelLayoutParameter = alarmLabelLayoutParameter;

export const productLabelStyle = new LabelStyle({
  verticalTextAlignment: 'center',
  horizontalTextAlignment: 'left',
  font: new Font({
    fontFamily,
    fontSize: 14,
    fontWeight: '600'
  }),
  wrapping: 'wrap-word-ellipsis',
  textFill: '#323130',
  maximumSize: [180, 60]
});

const colors = PRODUCT_COLORS;

const colorsLight = PRODUCT_COLORS_LIGHT;

export function getProductColor(
  product: Product,
  products: Product[],
  light: boolean = false
): FillConvertible {
  let index = products.indexOf(product);
  if (index === -1) {
    index = 0;
    console.error('unable to find product', product);
  }
  if (light) {
    return colorsLight[index % colors.length];
  }
  return colors[index % colors.length];
}

export function getProductNodeStyle(product: Product, products: Product[]): GroupNodeStyle {
  const stroke = `${getProductColor(product, products)}`;
  const fill = `${getProductColor(product, products, true)}`;
  return new GroupNodeStyle({
    stroke,
    tabFill: stroke,
    contentAreaFill: fill,
    tabPosition: 'right',
    tabHeight: 5,
    cornerRadius: 5,
    tabPadding: 1
  });
}

export function getProductEdgeStyle(product: Product, products: Product[], maxRevenue: number) {
  const revenue = getRevenue(product);

  let thickness = constantEdgeWidth;
  if (variableEdges) {
    thickness = Math.floor((maximumEdgeWidth * revenue) / maxRevenue);
  }
  return new PolylineEdgeStyle({
    targetArrow: new Arrow('none'),
    stroke: new Stroke(Fill.from(getProductColor(product, products)), thickness),
    smoothingLength: 10
  });
}

/**
 * Creates an animated edge style with a moving dot for product flows.
 * @param product - The product for color styling
 * @param products - All products for color indexing
 * @param maxRevenue - Maximum revenue for thickness scaling
 * @param pixelsPerSecond - Base speed in pixels per second (default 100)
 * @returns DotEdgeStyle with animated dot
 */
export function getAnimatedProductEdgeStyle(
  product: Product,
  products: Product[],
  maxRevenue: number,
  pixelsPerSecond?: number
): DotEdgeStyle {
  const revenue = getRevenue(product);
  const color = String(getProductColor(product, products));

  let thickness = constantEdgeWidth;
  if (variableEdges) {
    thickness = Math.floor((maximumEdgeWidth * revenue) / maxRevenue);
  }

  return new DotEdgeStyle(color, thickness, color, 4, pixelsPerSecond ?? 100);
}

export function getEdgeThickness(edge: IEdge): number {
  if (edge.style instanceof DotEdgeStyle) {
    return edge.style.strokeWidth;
  }
  return (edge.style as PolylineEdgeStyle).stroke!.thickness;
}

export const backgroundStyle = new PolylineEdgeStyle({
  stroke: new Stroke(EDGE.BACKGROUND, edgeBackgroundStrokeWidth),
  smoothingLength: 10
});

export const searchNodeStyleIndicatorRenderer = new NodeStyleIndicatorRenderer({
  nodeStyle: new RectangleNodeStyle({
    cornerSize: 4,
    stroke: `4px ${UI.HIGHLIGHT.SEARCH}`,
    fill: UI.BACKGROUND.TRANSPARENT
  }),
  margins: 2,
  zoomPolicy: 'view-coordinates'
});
